import type { BookScope, BookType, PostingMode, RowStatus } from '../generated/enums.js';
import type { InitialPeriodRange } from './periods.js';

// GL Books – wire contracts

/**
 * The one accounting framework whose books default to MEMO posting mode. Mirrors the
 * `gl_book_derive_posting_mode` database trigger (the authority on the rule): a
 * TAX_BASIS book measures without reaching the ledger, so it is never linked to an
 * entity and needs no chart-of-accounts assignment or open periods.
 */
export const MEMO_ACCOUNTING_FRAMEWORK_CODE = 'TAX_BASIS';

interface CreateBookRequestBase {
  code: string;
  name: string;
  type: BookType;
  scope: BookScope;
  reportingCurrency: string;
  functionalCurrency?: string;
  /** Posting rules authority for this book (for example IFRS, US_GAAP, MX_NIF). */
  frameworkCode?: string;
  /**
   * Whether this book's measurements reach the general ledger. Omit to take the
   * default derived from `frameworkCode` — MEMO for a tax-basis book, POSTED for
   * everything else. A MEMO book is never linked to an entity, so `entityId` and
   * `calendarId` are ignored for one.
   */
  postingMode?: PostingMode;
  status?: RowStatus;
}

/** A book with no entity link: no posting scope is provisioned. */
export interface CreateStandaloneBookRequest extends CreateBookRequestBase {
  entityId?: undefined;
  calendarId?: undefined;
  coaVersionId?: undefined;
  periodRange?: undefined;
}

/**
 * A book linked to an entity at creation. A POSTED book link provisions the entity's
 * posting scope in the same transaction, so the chart of accounts is mandatory: a
 * PUBLISHED CoA version the scope will post against. For a MEMO (tax-basis) book
 * prefer {@link CreateStandaloneBookRequest} — the server ignores the link anyway.
 */
export interface CreateEntityLinkedBookRequest extends CreateBookRequestBase {
  entityId: string;
  calendarId: string;
  /** PUBLISHED CoA version assigned to the new (entity, book) posting scope. */
  coaVersionId: string;
  /** Inclusive calendar-period range to open. Omit to open the current fiscal year. */
  periodRange?: InitialPeriodRange;
}

export type CreateBookRequest = CreateStandaloneBookRequest | CreateEntityLinkedBookRequest;

/**
 * Wire-permissive shape for the server's validation pipe: whether the link fields are
 * required depends on the posting mode the database derives after the row exists, so
 * the DTO accepts everything optional and the service enforces the rule at runtime.
 * Typed clients should build {@link CreateBookRequest} instead.
 */
export interface CreateBookInput extends CreateBookRequestBase {
  entityId?: string;
  calendarId?: string;
  coaVersionId?: string;
  periodRange?: InitialPeriodRange;
}

/**
 * Posting mode is deliberately not updatable. Switching a live book to MEMO is a
 * cut-over decision about the balances already posted under it, not a field edit,
 * and the database refuses it while the book is provisioned for any entity.
 */
export type UpdateBookRequest = Partial<
  Omit<CreateBookRequestBase, 'postingMode'> & Pick<CreateBookInput, 'entityId' | 'calendarId'>
>;

export interface BookResponse {
  bookId: string;
  code: string;
  name: string;
  type: BookType;
  scope: BookScope;
  reportingCurrency: string;
  functionalCurrency?: string | null;
  frameworkCode: string;
  postingMode: PostingMode;
  status: RowStatus;
  /** True once this book has posted or reversed journal entries and is immutable. */
  hasPostings?: boolean;
}

export interface AccountingFrameworkResponse {
  code: string;
  name: string;
  description?: string | null;
}

export interface ListBooksQuery {
  entityId?: string;
  scope?: BookScope;
}

// GL Entity-Book Links – wire contracts

export interface EntityBookResponse {
  tenantId: string;
  entityId: string;
  bookId: string;
  calendarId: string;
  functionalCurrency: string;
}

export interface CreateEntityBookRequest {
  entityId: string;
  bookId: string;
  calendarId: string;
  functionalCurrency: string;
}

export interface ListEntityBooksQuery {
  entityId?: string;
  bookId?: string;
}
